import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  getCurrentUser,
  signOut,
  signInWithRedirect,
  fetchAuthSession,
  AuthUser,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { authConfigEnabled } from '../config/auth';

// Custom IdP name registered in the Cognito user pool. Must match
// GOOGLE_WORKSPACE_SAML_PROVIDER_NAME in cdk/lib/constructs/auth.ts.
export const GOOGLE_WORKSPACE_PROVIDER = 'GoogleWorkspace';

export interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (provider?: typeof GOOGLE_WORKSPACE_PROVIDER) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

// Dev bypass: when Cognito is not configured (no user pool ID), auto-authenticate.
// Source of truth is the hostname-driven config in config/auth.ts.
const DEV_BYPASS = !authConfigEnabled;

const DEV_USER: AuthUser = {
  username: 'dev-user',
  userId: 'dev-user-id',
  signInDetails: { loginId: 'dev@bsidesboulder.com', authFlowType: 'USER_SRP_AUTH' },
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(DEV_BYPASS ? DEV_USER : null);
  const [isLoading, setIsLoading] = useState(!DEV_BYPASS);

  const checkUser = useCallback(async () => {
    if (DEV_BYPASS) {
      setUser(DEV_USER);
      setIsLoading(false);
      return;
    }
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      // Store token for API requests after confirming user is logged in
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (token) {
        localStorage.setItem('auth_token', token);
      }
    } catch {
      setUser(null);
      localStorage.removeItem('auth_token');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (DEV_BYPASS) return;
    checkUser();

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          checkUser();
          break;
        case 'signedOut':
          setUser(null);
          break;
        case 'tokenRefresh':
          checkUser();
          break;
      }
    });

    return () => unsubscribe();
  }, [checkUser]);

  const login = async (provider?: typeof GOOGLE_WORKSPACE_PROVIDER) => {
    if (DEV_BYPASS) return;
    if (provider) {
      await signInWithRedirect({ provider: { custom: provider } });
      return;
    }
    await signInWithRedirect();
  };

  const logout = async () => {
    if (DEV_BYPASS) {
      setUser(null);
      return;
    }
    try {
      await signOut();
      setUser(null);
      // Clear any stored tokens
      localStorage.removeItem('auth_token');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getToken = async (): Promise<string | null> => {
    if (DEV_BYPASS) return 'dev-token';
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || null;
      // Store token for API requests
      if (token) {
        localStorage.setItem('auth_token', token);
      }
      return token;
    } catch {
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
